import { Fragment, ReactElement } from 'react';
import { useFlags } from '@unleash/proxy-client-react';

import { Navigate, Route, Routes } from 'react-router-dom';
import AppLayout from '@common/layouts/AppLayout';
import OnboardingLayout from '@common/layouts/OnboardingLayout';
import ErrorPage from '@common/pages/ErrorPage';

import RequestsLayout from '@MyRequests/layouts/RequestsLayout';
import OpenRequestsPage from '@MyRequests/pages/OpenRequestsPage';
import ClosedRequestsPage from '@MyRequests/pages/ClosedRequestsPage';

import TasksPage from '@MyTasks/pages/TasksPage';

import BundleRequestPage from '@NewRequest/pages/BundleRequestPage';
import NewRequestPage from '@NewRequest/pages/NewRequestPage';
import TasksLayout from '@MyTasks/layouts/TasksLayout';
import NewRequestLayout from '@NewRequest/layouts/NewRequestLayout';
import TemplatesPage from '@MyRequests/pages/TemplatesPage';

import OnboardingAccessRequestFormPage from '@Onboarding/pages/OnboardingAccessRequestFormPage';
import OnboardingAccessRequestPage from '@Onboarding/pages/OnboardingAccessRequestPage';
import OnboardingAccessRequestFetchingPage from '@Onboarding/pages/OnboardingAccessRequestFetchingPage';
import MyAccessPage from './MyAccess/pages/MyAccessPage';
import { getFlagFixed, useFlagFixed } from '@common/utils/flags/use-flag-fixed';
import { Flags } from '@common/utils/flags/feature-flags';
import MyAccessLayout from './MyAccess/layouts/MyAccessLayout';

type RouteConfig = {
  layout?: ReactElement;
  index?: boolean;
  path?: string;
  element?: ReactElement;
  routes?: RouteConfig[];
  routeFlag?: Flags;
};

const routes = {
  OpenRequests: {
    label: 'Open Requests',
    path: '/requests/open',
    element: <OpenRequestsPage />,
  },
  ClosedRequests: {
    label: 'Closed Requests',
    path: '/requests/closed',
    element: <ClosedRequestsPage />,
  },
  NewRequest: {
    label: 'Integration Request',
    path: '/new-request/integration',
    element: <NewRequestPage />,
  },
  BundleRequest: {
    label: 'Bundle Request',
    path: '/new-request/bundle',
    element: <BundleRequestPage />,
  },
  Tasks: {
    label: 'My Tasks',
    path: '/tasks',
    element: <TasksPage />,
  },
  Templates: {
    label: 'Favorites',
    path: '/requests/favorites',
    element: <TemplatesPage />,
  },
  OnboardingIntegrationRequest: {
    label: 'Onboarding Integration Request',
    path: '/onboarding-steps/integration-request',
    element: <OnboardingAccessRequestFormPage />,
  },
  OnboardingAccessRequestFetching: {
    label: 'Onboarding Integration Request Fetching',
    path: '/onboarding-steps/access-request-fetching',
    element: <OnboardingAccessRequestFetchingPage />,
  },
  OnboardingAccessRequest: {
    label: 'Onboarding Integration Request',
    path: '/onboarding-steps/access-request/:id',
    element: <OnboardingAccessRequestPage />,
  },
  MyAccess: {
    label: 'My Access',
    path: '/my-access',
    element: <MyAccessPage />,
    routeFlag: Flags.PORTAL_MY_ACCESS,
  },
};

export default routes;

const routesConfig: RouteConfig[] = [
  {
    layout: <AppLayout />,
    routes: [
      { index: true, path: '/', element: <HomePage /> },
      {
        layout: <RequestsLayout />,
        routes: [routes.OpenRequests, routes.ClosedRequests, routes.Templates],
      },
      {
        layout: <TasksLayout />,
        routes: [routes.Tasks],
      },
      {
        layout: <NewRequestLayout />,
        routes: [routes.NewRequest, routes.BundleRequest],
      },
      {
        layout: <MyAccessLayout />,
        routes: [routes.MyAccess],
      },
      { path: '*', element: <ErrorPage errorCode={404} /> },
    ],
  },
  {
    layout: <OnboardingLayout />,
    routes: [
      routes.OnboardingIntegrationRequest,
      routes.OnboardingAccessRequestFetching,
      routes.OnboardingAccessRequest,
      {
        path: '*',
        element: <ErrorPage errorCode={404} />,
      },
    ],
  },
];

export function AppRoutes() {
  const flags = useFlags();

  const generateRoutes = (config: RouteConfig[]): ReactElement[] => {
    return config.map((route, index) => {
      const keyProp = route.path || `route-${index}`;

      if (route.layout) {
        return (
          <Route key={keyProp} element={route.layout}>
            {route.routes && generateRoutes(route.routes)}
          </Route>
        );
      }

      if (route.routeFlag) {
        const routeFlag = getFlagFixed(flags, route.routeFlag);
        if (!routeFlag.isEnabled) {
          return <Fragment key={keyProp} />;
        }
      }

      return <Route key={keyProp} {...route} />;
    });
  };

  return <Routes>{generateRoutes(routesConfig)}</Routes>;
}

function HomePage() {
  const { isEnabled } = useFlagFixed(Flags.PORTAL_MY_ACCESS);
  return <Navigate to={isEnabled ? routes.MyAccess.path : routes.OpenRequests.path} replace />;
}
